import React, { useEffect, useState } from "react";
import { X, ExternalLink, Globe, Layers, Server, Palette, FileText, CalendarDays } from "lucide-react";

export const ProjectModal = ({ project, onClose }) => {
  const [coverFailed, setCoverFailed] = useState(false);

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [project, onClose]);

  useEffect(() => setCoverFailed(false), [project]);

  if (!project) return null;
  const isSeoCaseStudy = project.type === "seo" || Boolean(project.pdf);
  const shot = project.url
    ? `https://s.wordpress.com/mshots/v1/${encodeURIComponent(project.url)}?w=1200&h=780`
    : "";
  const showScreenshot = project.type === "website" && project.hideScreenshot !== true && Boolean(project.url);
  const showCover = isSeoCaseStudy && project.cover && !coverFailed;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-testid="project-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-[hsl(var(--brand-navy)/.75)] backdrop-blur-sm" />
      <div className="relative z-10 w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="project-modal-close" className="absolute top-4 right-4 z-20 h-9 w-9 rounded-full bg-white/90 hover:bg-white grid place-items-center border border-black/5" aria-label="Close">
          <X className="h-4 w-4" />
        </button>

        <div className="relative aspect-[16/9] w-full overflow-hidden bg-lilac rounded-t-3xl">
          {showCover ? (
            <img src={project.cover} alt={`${project.name} SEO case study cover`} onError={() => setCoverFailed(true)} className="w-full h-full object-contain bg-slate-100" />
          ) : showScreenshot ? (
            <img src={shot} alt={`${project.name} website preview`} className="w-full h-full object-cover object-top" />
          ) : (
            <div className="brand-gradient w-full h-full relative flex items-center justify-center p-8 text-center">
              <div className="relative z-10">
                <div className="font-[Fraunces] text-4xl font-bold text-white">{project.name}</div>
                <div className="mt-2 text-white/85 text-sm">{project.domain}</div>
              </div>
              <div className="absolute inset-0 noise-overlay" />
            </div>
          )}
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {(project.categories || []).map((c) => <span key={c} className="text-[11px] font-medium rounded-full bg-lilac text-brand-deep px-2.5 py-0.5">{c}</span>)}
            {project.platform && <span className="text-[11px] font-semibold rounded-full bg-[hsl(var(--brand-amber)/.2)] text-amber-brand px-2.5 py-0.5 uppercase">{project.platform}</span>}
          </div>
          <h3 className="font-[Fraunces] text-3xl sm:text-4xl font-bold text-[hsl(var(--brand-navy))]">{project.name}</h3>
          <p className="mt-1 text-brand-deep font-medium">{project.niche}</p>
          {project.duration && <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500"><CalendarDays className="h-3.5 w-3.5" />{project.duration}</p>}
          <p className="mt-4 text-slate-600 leading-relaxed">{project.description}</p>

          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {project.frontend && <TechBlock icon={Palette} label="Frontend" items={project.frontend} />}
            {project.backend && <TechBlock icon={Server} label="Backend" items={project.backend} />}
            {project.otherSkills && <TechBlock icon={Layers} label="Other Skills" items={project.otherSkills} />}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {isSeoCaseStudy && project.pdf ? (
              <a href={project.pdf} target="_blank" rel="noopener noreferrer" data-testid="modal-report-btn" className="group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-navy))] hover:bg-brand text-white px-5 py-2.5 text-sm font-semibold transition">
                <FileText className="h-4 w-4" /> View Full PDF Report <ExternalLink className="h-4 w-4" />
              </a>
            ) : project.url ? (
              <a href={project.url} target="_blank" rel="noopener noreferrer" data-testid="modal-visit-btn" className="group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-navy))] hover:bg-brand text-white px-5 py-2.5 text-sm font-semibold transition">
                <Globe className="h-4 w-4" />
                {project.type === "website" ? "Visit Live Website" : project.type === "figma" ? "Open Figma Design" : project.platform === "iOS" ? "Open on App Store" : "Open on Play Store"}
                <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            ) : null}
            {project.domain && <div className="inline-flex items-center gap-1.5 text-xs text-slate-500 px-2">{project.domain}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

const TechBlock = ({ icon: Icon, label, items }) => (
  <div className="rounded-2xl border border-black/5 bg-lilac/50 p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-brand-deep font-semibold"><Icon className="h-3.5 w-3.5" /> {label}</div>
    <div className="mt-2 flex flex-wrap gap-1.5">{items.map((t) => <span key={t} className="text-[11px] font-medium rounded-md bg-white text-[hsl(var(--brand-navy))] px-2 py-0.5 border border-black/5">{t}</span>)}</div>
  </div>
);

export default ProjectModal;
